import React, { useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import Filter from '@intelligence/components/Filter';
import useFilterExtend from '@intelligence/hooks/useFilterExtend';
import useGenreComponent from '@intelligence/hooks/useGenreComponent';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import useUpdateFiltersCfg from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Filter/_hooks/useUpdateFiltersCfg';
import useUpdateFiltersFormValue from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Filter/_hooks/useUpdateFiltersFormValue';
import { getInitialFormValue } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Filter/utils';
import { GENRE_SOURCES_NEW } from '@intelligence/components/GenreSelect/constants';
import { STATIC_FILTER_CONFIGS } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Filter/_config';
import {
  // 单选、多选国家筛选器
  COMPOSED_COUNTRY_SELECT as FILTER_COMPOSED_COUNTRY_SELECT,
  // 时间筛选器的key
  RANGE_TIME as FILTER_RANGE_TIME,
  // 品类筛选器
  GENRETOR as FILTER_GENRE_SELECT,
  // Mobile Overlap 筛选器
  MOBILE_OVERLAP as FILTER_MOBILE_OVERLAP,
} from '@intelligence/components/Filter/FilterBase/_config';

import type { IBaseProps, IRegion } from '@intelligence/types/common';
import type { IMobileDetailsOverlapFilterParams } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/types';

interface IMobileDetailsOverlapFilterProps extends IBaseProps {
  loading?: boolean;
  entityType?: string;
  rangePickerDisabledMap?: IDateRangeDisabledCallbackDateTypeMap | null;
  regionsOptions: IRegion[];
  genreOptions?: any;
  fetchParams: IMobileDetailsOverlapFilterParams;
  buriedPointParams?: any;
  onConfirm: (payload: IMobileDetailsOverlapFilterParams) => void;
  onReset?: () => void;
  onInit?: () => void;
}

const MobileDetailsOverlapFilter: React.FC<IMobileDetailsOverlapFilterProps> = (props) => {
  const {
    loading,
    entityType,
    rangePickerDisabledMap,
    regionsOptions,
    genreOptions,
    fetchParams,
    buriedPointParams,
    onConfirm,
    onReset,
    onInit,
  } = props;

  const { formatMessage } = useIntlExtend();
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});

  const {
    genreRef,
    genreSource,
    mainGenres,
    subGenres,
    handleGenresChange,
    handleReset: handleGenresReset,
  } = useGenreComponent(props);

  // 动态筛选器配置，即全部或者部分配置 需要通过组件props、组件其他state、组件其他变量来决定，不能单独写在一个js文件中
  const dynamicFilterConfigs = {
    genre: {
      savePopupVisble: 1,
      key: FILTER_GENRE_SELECT,
      type: FILTER_GENRE_SELECT,
      overlayClassName: 'cascade-mode',
      visible: 1,
      disabled: false,
      // source字段 仅在disableIegg或disableAnnie启用时，才能使用，否则会导致source异常
      // source: 'iegg',
      // isMarketProfilMobile: true,
      disableIegg: false,
      disableAnnie: false,
      disableSensorTower: false,
      isStdGenreValuesHandler: true,
      sources: GENRE_SOURCES_NEW,
      genresOptionsDataLoading: loading,
      genresOptionsData: genreOptions,
      // addOthersInAnnir: true,
      genreData: {
        genreRef,
        genreSource,
        mainGenres,
        subGenres,
        handleGenresChange,
        handleGenresReset,
      },
      entityType,
      buriedPointParams,
      // 需要外面传入选项数据时，用这个字段，品类组件内就不会自动调接口获取选项数据了
      // genresOptionsData: {
      //   ieg: [],
      //   annir: [],
      // },
    },
  };

  const [inputList, setInputList] = useState([
    STATIC_FILTER_CONFIGS.RANGE_TIME,
    STATIC_FILTER_CONFIGS.COMPOSED_COUNTRY_SELECT,
    STATIC_FILTER_CONFIGS.MOBILE_OVERLAP,
    dynamicFilterConfigs.genre,
  ]);

  const [formValue, setFormValue] = useState(getInitialFormValue(isOperateDetail, detailFilterExtend));

  useUpdateFiltersCfg({
    loading,
    rangePickerDisabledMap,
    regionsOptions,
    countryFilterTip: formatMessage({ id: 'dbi_mobile_overlap_country_filter_tip' }),
    genreOptions,
    inputList,
    setInputList,
  });

  useUpdateFiltersFormValue({
    fetchParams,
    formValue,
    setFormValue,
  });

  const handleConfirm = useMemoizedFn((value, apiValue) => {
    setFormValue(value);
    const composedCountrySelectApiValue = apiValue?.[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
    const rangeTimeApiValue = apiValue?.[FILTER_RANGE_TIME];
    const genreSelectApiValue = apiValue?.[FILTER_GENRE_SELECT];
    const mobileOverlapApiValue = apiValue?.[FILTER_MOBILE_OVERLAP]?.value;

    const payload = {
      markets: composedCountrySelectApiValue?.countries,
      granularity: rangeTimeApiValue?.granularity || 'monthly',
      start_date: rangeTimeApiValue?.start_date || '',
      end_date: rangeTimeApiValue?.end_date || '',
      genre_source: genreSelectApiValue?.source || 'iegg',
      iegg_genre: genreSelectApiValue?.iegg_genre || [],
      sensortower_genre: genreSelectApiValue?.sensortower_genre || [],
      appannie_genre: genreSelectApiValue?.appannie_genre || [],
      mobile_overlap: mobileOverlapApiValue,
    };
    onConfirm?.(payload);
    setDetailFilterExtend(apiValue);
  });

  const handleReset = useMemoizedFn(() => {
    genreRef.current?.reset();
    onReset?.();
  });

  const handleInit = useMemoizedFn(() => {
    onInit?.();
  });

  return (
    <Filter
      style={{ marginBottom: 10 }}
      showButton={false}
      inputList={inputList}
      value={formValue}
      onConfirm={handleConfirm}
      onReset={handleReset}
      onInit={handleInit}
    />
  );
};

export default React.memo(MobileDetailsOverlapFilter);
